import { CommonModule } from '@angular/common';
import { Component, inject, Inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { BooleanDisplayPipe, CmCulture } from '@ats/cm/core';
import { AtsIconsModule } from '@ats/icons';
import { AtsNotificationService } from '@ats/notifications';
import { CultureService } from '@cm/core/culture';
import {
	getLocType2,
	LOC_INJECTOR,
	LocalizationDialogData,
	LocalizationProvider,
	LocalizationService,
	LocalizationVM,
} from '@cm/core/localization';
import { firstValueFrom, Observable, of, switchMap, tap } from 'rxjs';
import { take } from 'rxjs/operators';
import { LocalizePipe } from '../../pipes/localize.pipe';
import { sharedLocalizationLocs } from '../../shared.locs';
import { HtmlLocalizeFormComponent } from '../html-localize-form/html-localize-form.component';
import { DialogLocType, localizationModalLocs } from './localization-dialog.locs';

@Component({
	selector: 'cm-localization-dialog',
	templateUrl: './localization-dialog.component.html',
	styleUrls: ['./localization-dialog.component.scss'],
	imports: [
		CommonModule,
		LocalizePipe,
		BooleanDisplayPipe,
		MatDialogModule,
		MatButtonModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatSelectModule,
		MatInputModule,
		HtmlLocalizeFormComponent,
		AtsIconsModule,
	],
	providers: [LocalizationProvider({ ...localizationModalLocs, ...sharedLocalizationLocs })],
})
export class LocalizationDialogComponent {
	cultures$!: Observable<CmCulture[]>;
	currentValue$: Observable<string>;
	loc: LocalizationVM;
	form: FormGroup = this.getForm();
	hasCodeChanges = false;

	get language(): AbstractControl | null {
		return this.form.get('language') as AbstractControl;
	}

	get value(): AbstractControl | null {
		return this.form.get('value') as AbstractControl;
	}
	constructor(
		@Inject(LOC_INJECTOR) public readonly locs: DialogLocType,
		@Inject(MAT_DIALOG_DATA)
		private readonly data: LocalizationDialogData,
		private readonly dialogRef: MatDialogRef<LocalizationDialogComponent>,
		private readonly fb: FormBuilder,
		private readonly localizationService: LocalizationService,
		private readonly notifications: AtsNotificationService
	) {
		this.loc = this.data.loc;
		this.cultures$ = inject(CultureService).supportedCultures$;
		this.form = this.getForm();
		this.currentValue$ = !this.language
			? of(this.loc?.value || '')
			: this.language.valueChanges.pipe(
					switchMap(culture =>
						this.localizationService.getByCulture$(
							this.loc.name,
							culture,
							getLocType2(this.loc)
						)
					),
					tap(val => {
						if (this.value) {
							this.value.setValue(val);
						}
					})
			  );
	}

	handleCodeChanges(ev: boolean) {
		this.hasCodeChanges = ev;
	}

	remove(): void {
		this.localizationService
			.deleteClientLoc$({
				name: this.loc.name,
				portal: this.loc.portal,
				cultureName: this.language?.value || 'en',
			})
			.pipe(take(1))
			.subscribe(result => {
				if (result === null) {
					return;
				}

				this.dialogRef.close(false);
			});
	}

	handleSubmit(): void {
		this.form.markAllAsTouched();
		if (!this.form.valid) {
			firstValueFrom(
				this.localizationService.findValue$('Core/System.Global.Notification.NoChanges')
			).then(message =>
				this.notifications.popError({
					id: 'loc-update-error',
					message,
				})
			);
			return;
		}
		this.localizationService
			.createOrUpdateClientLoc$({
				name: this.loc.name,
				cultureName: this.language?.value,
				value: this.value?.value,
			})
			.pipe(take(1))
			.subscribe(() => {
				this.notifications.showSuccess(this.locs.CMSystemLocalizationDialogChangesSaved);
				// Doesn't matter if a loc is returned. In the table, CP locs aren't saved.
				this.dialogRef.close(this.hasCodeChanges);
			});
	}

	handleCancel(): void {
		this.dialogRef.close(false);
	}

	private getForm(): FormGroup {
		return this.fb.group({
			language: this.data.language,
			value: this.data.loc?.value || '',
		});
	}
}
